import { CustomHeaders } from '@portafino/shared'
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

// TODO: Move all of this to shared

interface ApiResponse<T> {
  data: T
  status: number
  statusText: string
  headers: unknown
  config: AxiosRequestConfig
  request?: unknown
}

export interface ApiErrorResponse {
  code: string
  message: string
}

export type ApiError = AxiosError<ApiErrorResponse>

export type ErrorInterceptor = (error: ApiError) => Promise<void>

class ApiClient {
  private client: AxiosInstance
  private errorInterceptor?: ErrorInterceptor

  constructor(baseUrl: string) {
    this.client = axios.create({
      baseURL: baseUrl,
    })
    this.client.interceptors.response.use((response) => response, this.handleError.bind(this))
  }

  public setErrorInterceptor(errorInterceptor: ErrorInterceptor): void {
    this.errorInterceptor = errorInterceptor
  }

  public setBearerToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  public clearBearerToken(): void {
    delete this.client.defaults.headers.common['Authorization']
  }

  public hasAppContextId(): boolean {
    return !!this.client.defaults.headers.common['x-app-context-id']
  }

  public hasBearerToken(): boolean {
    return !!this.client.defaults.headers.common['Authorization']
  }

  public isAuthReady(userType?: string): boolean {
    // For mocked users, we only need the context header
    if (userType === 'mocked') {
      return this.hasAppContextId()
    }

    // For real users, we need both context header and bearer token
    return this.hasAppContextId() && this.hasBearerToken()
  }

  public setApiKeyHeaders(apiKeyId: string, apiKey: string): void {
    this.client.defaults.headers.common[CustomHeaders.API_ID] = apiKeyId
    this.client.defaults.headers.common[CustomHeaders.API_KEY] = apiKey
  }

  public clearApiKeyHeaders(): void {
    delete this.client.defaults.headers.common['x-api-key-id']
    delete this.client.defaults.headers.common['x-api-key']
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.get(url, config)
    return this.formatResponse(response)
  }

  public async post<TResponse, TBody>(
    url: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<TResponse> = await this.client.post(url, data, config)
    return this.formatResponse(response)
  }

  public async put<TResponse, TBody>(
    url: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<TResponse> = await this.client.put(url, data, config)
    return this.formatResponse(response)
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<T> = await this.client.delete(url, config)
    return this.formatResponse(response)
  }

  public async patch<TResponse, TBody>(
    url: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response: AxiosResponse<TResponse> = await this.client.patch(url, data, config)
    return this.formatResponse(response)
  }

  private formatResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: response.config,
      request: response.request,
    }
  }

  private async handleError(error: ApiError): Promise<void> {
    if (this.errorInterceptor) {
      await this.errorInterceptor(error)
    }
    return Promise.reject(error)
  }
}

// TODO: Use env variable
export const apiClient = new ApiClient('/api')
export default ApiClient
